import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  FormControl, 
  FormLabel, 
  Button, 
  Box,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { obtenerEncuestaPorToken, enviarRespuestaEncuestaPublica } from '../../actions/EncuestaPublicaAction';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const EncuestaPublica = () => {
  const { token } = useParams();
  const [encuesta, setEncuesta] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const cargarEncuesta = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await obtenerEncuestaPorToken(token);
      setEncuesta(response.data);
      // Inicializar objeto de respuestas vacío
      const respuestasIniciales = {};
      response.data.preguntas.forEach(pregunta => {
        respuestasIniciales[pregunta.preguntaId] = '';
      });
      setRespuestas(respuestasIniciales);
    } catch (error) {
      console.error('Error al cargar encuesta:', error);
      setError('No se pudo cargar la encuesta. El enlace puede ser inválido o la encuesta ya no está disponible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarEncuesta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleRespuestaChange = (preguntaId, respuestaId) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: respuestaId
    }));
  };

  const validarFormulario = () => {
    // Verificar que todas las preguntas tengan respuesta
    return encuesta.preguntas.every(pregunta => respuestas[pregunta.preguntaId] !== '');
  };

  const handleEnviar = async () => {
    if (!validarFormulario()) {
      setError('Por favor, responda todas las preguntas antes de enviar.');
      return;
    }

    try {
      setEnviando(true);
      setError(null);
      
      // Formatear las respuestas para enviar al backend
      const respuestasFormateadas = Object.entries(respuestas).map(([preguntaId, respuestaId]) => ({
        preguntaId,
        respuestaId
      }));

      await enviarRespuestaEncuestaPublica(token, {
        encuestaId: encuesta.encuestaId,
        respuestas: respuestasFormateadas
      });

      setEnviado(true);
    } catch (error) {
      console.error('Error al enviar respuestas:', error);
      setError('Hubo un error al enviar sus respuestas. Por favor, intente nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (enviado) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Card elevation={3} sx={{ width: '100%', textAlign: 'center', p: 4 }}>
          <CardContent>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              ¡Gracias por su tiempo!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sus respuestas han sido registradas exitosamente.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error && !encuesta) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Card elevation={3} sx={{ width: '100%', textAlign: 'center', p: 4 }}>
          <CardContent>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error">
              Error al cargar la encuesta
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, minHeight: '100vh' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          {encuesta?.titulo}
        </Typography>
        
        <Divider sx={{ my: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" noValidate>
          {encuesta?.preguntas.map((pregunta, index) => (
            <Box key={pregunta.preguntaId} sx={{ mb: 4 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 500 }}>
                  {index + 1}. {pregunta.texto}
                </FormLabel>
                <RadioGroup
                  value={respuestas[pregunta.preguntaId] || ''}
                  onChange={(e) => handleRespuestaChange(pregunta.preguntaId, e.target.value)}
                >
                  {pregunta.respuestas.map((respuesta) => (
                    <FormControlLabel
                      key={respuesta.respuestaId}
                      value={respuesta.respuestaId}
                      control={<Radio />}
                      label={respuesta.texto}
                      sx={{ 
                        ml: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          borderRadius: 1
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleEnviar}
              disabled={enviando}
              sx={{ minWidth: 200 }}
            >
              {enviando ? <CircularProgress size={24} /> : 'Enviar Respuestas'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EncuestaPublica;
